import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  List,
  Result,
  Select,
  Skeleton,
  Tag,
  Typography,
  Upload
} from "antd";
import { CheckCircleOutlined, UploadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { getPublicRequest, uploadPublicDocument } from "./api";
import { STATUS_COLORS, formatDateTime, statusLabels } from "./format";
import "./subcontractors.css";

const ACCEPT = "application/pdf,image/jpeg,image/png,image/webp";

/** The subcontractor's page behind the upload link: no login, uploads wait for review. */
const PublicUpload = () => {
  const { t, i18n } = useTranslation("dashboard");
  const { token } = useParams();
  const domain = new URLSearchParams(useLocation().search).get("domain") || "";
  const [form] = Form.useForm();
  const [state, setState] = useState({ loading: true });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const labels = statusLabels(t);

  useEffect(() => {
    getPublicRequest(token, domain)
      .then(res => setState({ loading: false, view: res.data.data }))
      .catch(() => setState({ loading: false, failed: true }));
  }, [token, domain]);

  const submit = async values => {
    if (!file) {
      setError(t("Please choose a PDF or a photo."));
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res = await uploadPublicDocument(
        token,
        domain,
        {
          type: values.type,
          number: values.number,
          issuer: values.issuer,
          validUntil: values.validUntil ? values.validUntil.format("YYYY-MM-DD") : ""
        },
        file
      );
      setState(prev => ({ ...prev, view: res.data.data }));
      form.resetFields();
      setFile(null);
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.errorMessage || t("The upload failed. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const view = state.view;
  return (
    <div className="subcontractors-page sc-public" dir={i18n.dir?.()}>
      <div className="sc-public-inner">
        {state.loading ? (
          <Card>
            <Skeleton active paragraph={{ rows: 6 }} />
          </Card>
        ) : state.failed ? (
          <Card>
            <Result
              status="warning"
              title={t("This link is not valid or has expired.")}
              subTitle={t("Please ask the company for a new link.")}
            />
          </Card>
        ) : (
          <>
            <div className="sc-public-company">
              {view.company.logo ? <img src={view.company.logo} alt="" /> : null}
              <Typography.Text strong>{view.company.name}</Typography.Text>
            </div>
            <Card className="mb-3">
              <Typography.Title level={4}>{t("Please upload your documents")}</Typography.Title>
              <Typography.Paragraph type="secondary">
                {t("{{company}} asks {{name}} for the following documents:", {
                  company: view.company.name,
                  name: view.subcontractor.name
                })}
              </Typography.Paragraph>
              <List
                size="small"
                dataSource={view.types}
                renderItem={type => (
                  <List.Item key={type.key}>
                    <span>{type.label}</span>
                    <Tag color={STATUS_COLORS[type.status]}>{labels[type.status]}</Tag>
                  </List.Item>
                )}
              />
            </Card>
            {done && (
              <Alert
                className="mb-3"
                type="success"
                showIcon
                icon={<CheckCircleOutlined />}
                message={t("Thank you, the document was uploaded.")}
                description={t("You can upload more documents with the same link.")}
              />
            )}
            {view.remaining > 0 ? (
              <Card className="mb-3" title={t("Upload a document")}>
                <Form form={form} layout="vertical" onFinish={submit}>
                  <Form.Item name="type" label={t("Document")} rules={[{ required: true }]}>
                    <Select options={view.types.map(x => ({ value: x.key, label: x.label }))} />
                  </Form.Item>
                  <Form.Item label={t("File (PDF or photo, max. 10 MB)")} required>
                    <Upload
                      accept={ACCEPT}
                      maxCount={1}
                      beforeUpload={selected => {
                        setFile(selected);
                        return false;
                      }}
                      onRemove={() => setFile(null)}
                      fileList={file ? [{ uid: "1", name: file.name, status: "done" }] : []}
                    >
                      <Button icon={<UploadOutlined />}>{t("Choose file")}</Button>
                    </Upload>
                  </Form.Item>
                  <Form.Item name="number" label={t("Number (if printed)")}>
                    <Input maxLength={100} />
                  </Form.Item>
                  <Form.Item name="issuer" label={t("Issued by")}>
                    <Input maxLength={200} />
                  </Form.Item>
                  <Form.Item name="validUntil" label={t("Valid until (if printed)")}>
                    <DatePicker className="sc-full" format="DD.MM.YYYY" />
                  </Form.Item>
                  {error && <Alert className="mb-3" type="error" showIcon message={error} />}
                  <Button type="primary" htmlType="submit" loading={busy} block>
                    {t("Upload")}
                  </Button>
                </Form>
              </Card>
            ) : (
              <Alert
                className="mb-3"
                type="info"
                showIcon
                message={t("No more uploads are possible with this link.")}
              />
            )}
            {view.uploads.length > 0 && (
              <Card className="mb-3" title={t("Uploaded")}>
                <List
                  size="small"
                  dataSource={view.uploads}
                  renderItem={(u, index) => (
                    <List.Item key={index}>
                      <span className="sc-break">
                        {u.label} · {u.fileName}
                      </span>
                      <span className="sc-small">
                        {formatDateTime(u.uploadedAt, i18n.language)}
                      </span>
                    </List.Item>
                  )}
                />
              </Card>
            )}
            <Typography.Paragraph type="secondary" className="sc-small">
              {t("Valid until {{date}}", { date: formatDateTime(view.expiresAt, i18n.language) })}
            </Typography.Paragraph>
          </>
        )}
      </div>
    </div>
  );
};

export default PublicUpload;
